import React, { useEffect, useRef, useState } from 'react';
import { useLang } from '../../i18n/LanguageContext';

const STEPS = [
  {
    num: '01',
    titleKey: 'landing.about.step1Title',
    descKey: 'landing.about.step1Desc',
    color: '#221C51',
    bg: 'rgba(34,28,81,0.07)',
    border: 'rgba(34,28,81,0.15)',
  },
  {
    num: '02',
    titleKey: 'landing.about.step2Title',
    descKey: 'landing.about.step2Desc',
    color: '#1877f2',
    bg: 'rgba(24,119,242,0.06)',
    border: 'rgba(24,119,242,0.15)',
  },
  {
    num: '03',
    titleKey: 'landing.about.step3Title',
    descKey: 'landing.about.step3Desc',
    color: '#059669',
    bg: 'rgba(5,150,105,0.06)',
    border: 'rgba(5,150,105,0.15)',
  },
  {
    num: '04',
    titleKey: 'landing.about.step4Title',
    descKey: 'landing.about.step4Desc',
    color: '#D97706',
    bg: 'rgba(217,119,6,0.06)',
    border: 'rgba(217,119,6,0.15)',
  },
];

const STATS = [
  { labelKey: 'landing.about.statTime', value: '4 h', color: '#221C51', bg: '#F5F7FF', border: '#E0E4F0' },
  { labelKey: 'landing.about.statPlatforms', value: '5+', color: '#1877f2', bg: '#EEF4FF', border: '#BFDBFE' },
  { labelKey: 'landing.about.statCampaigns', value: '5K+', color: '#059669', bg: '#ECFDF5', border: '#A7F3D0' },
  { labelKey: 'landing.about.statClients', value: '200+', color: '#D97706', bg: '#FFFBEB', border: '#FDE68A' },
];

// Accents des 4 étapes, éclaircis pour rester lisibles sur fond sombre.
const STEP_ACCENT = ['#a78bfa', '#7dd3fc', '#5eead4', '#fbbf24'];

const About = () => {
  const { t } = useLang();
  const [visible, setVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { threshold: 0.1 });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="about" ref={ref} style={{ background: '#0A0722', padding: '96px 24px', position: 'relative', overflow: 'hidden' }}>
      <div className="oba-aurora" style={{ top: '-14%', left: '-10%', width: 560, height: 560, background: 'radial-gradient(circle, rgba(109,93,251,.24) 0%, transparent 68%)' }} />
      <div style={{ maxWidth: 1100, margin: '0 auto' }}>

        {/* About us */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px,1fr))', gap: 64, alignItems: 'center', marginBottom: 96 }}>
          <div style={{ opacity: visible ? 1 : 0, transform: visible ? 'none' : 'translateY(24px)', transition: 'all 0.7s ease' }}>
            <span style={{ display: 'inline-block', padding: '5px 16px', borderRadius: 999, background: 'rgba(109,93,251,0.14)', border: '1px solid rgba(109,93,251,0.38)', color: '#c7bfff', fontSize: 12, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 24 }}>
              {t('landing.about.badge')}
            </span>
            <h2 style={{ fontSize: 'clamp(28px,4vw,42px)', fontWeight: 800, color: '#ffffff', letterSpacing: '-0.02em', marginBottom: 20, lineHeight: 1.15 }}>
              {t('landing.about.title1')}{' '}
              <span style={{ background: 'linear-gradient(120deg,#a78bfa,#818cf8,#38bdf8)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>{t('landing.about.title2')}</span>
            </h2>
            <p style={{ fontSize: 15, color: 'rgba(255,255,255,0.62)', lineHeight: 1.75, marginBottom: 16 }}>
              <span dangerouslySetInnerHTML={{ __html: t('landing.about.p1') }} />
            </p>
            <p style={{ fontSize: 15, color: 'rgba(255,255,255,0.62)', lineHeight: 1.75 }}>
              <span dangerouslySetInnerHTML={{ __html: t('landing.about.p2') }} />
            </p>
          </div>

          {/* Stat cards */}
          <div style={{ opacity: visible ? 1 : 0, transform: visible ? 'none' : 'translateY(24px)', transition: 'all 0.7s 0.15s ease', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            {STATS.map((item, i) => (
              <div key={i} style={{
                padding: '22px 18px', borderRadius: 14,
                background: 'linear-gradient(160deg, rgba(24,19,60,.72), rgba(11,8,32,.88))',
                border: '1px solid rgba(255,255,255,0.09)',
                boxShadow: '0 20px 50px -20px rgba(0,0,0,0.65)',
              }}>
                <div style={{ fontSize: 28, fontWeight: 800, color: STEP_ACCENT[i] || '#a78bfa', marginBottom: 6 }}>{item.value}</div>
                <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.62)', lineHeight: 1.4 }}>{t(item.labelKey)}</div>
              </div>
            ))}
          </div>
        </div>

        {/* How it works */}
        <div id="how-it-works">
          <div style={{ textAlign: 'center', marginBottom: 56 }}>
            <span style={{ display: 'inline-block', padding: '5px 16px', borderRadius: 999, background: 'rgba(56,189,248,0.13)', border: '1px solid rgba(56,189,248,0.38)', color: '#7dd3fc', fontSize: 12, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 20 }}>
              {t('landing.about.howBadge')}
            </span>
            <h2 style={{ fontSize: 'clamp(26px,4vw,40px)', fontWeight: 800, color: '#ffffff', letterSpacing: '-0.02em' }}>
              {t('landing.about.howTitle1')}{' '}
              <span style={{ background: 'linear-gradient(120deg,#a78bfa,#818cf8,#38bdf8)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>{t('landing.about.howTitle2')}</span>
            </h2>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px,1fr))', gap: 20 }}>
            {STEPS.map((s, i) => (
              <div key={i} style={{
                padding: '28px 24px', borderRadius: 16,
                background: 'linear-gradient(160deg, rgba(24,19,60,.72), rgba(11,8,32,.88))',
                border: '1px solid rgba(255,255,255,0.09)',
                boxShadow: '0 24px 60px -22px rgba(0,0,0,0.7)',
                opacity: visible ? 1 : 0,
                transform: visible ? 'none' : 'translateY(24px)',
                transition: `all 0.6s ${i * 100 + 200}ms ease`,
              }}>
                <div style={{ fontSize: 13, fontWeight: 800, color: STEP_ACCENT[i] || '#a78bfa', letterSpacing: '0.05em', marginBottom: 14 }}>{s.num}</div>
                <div style={{ width: 32, height: 3, background: STEP_ACCENT[i] || '#a78bfa', borderRadius: 2, marginBottom: 18, opacity: 0.5 }} />
                <h4 style={{ fontSize: 15, fontWeight: 700, color: '#ffffff', marginBottom: 10 }}>{t(s.titleKey)}</h4>
                <p style={{ fontSize: 13.5, color: 'rgba(255,255,255,0.62)', lineHeight: 1.65 }}>{t(s.descKey)}</p>
              </div>
            ))}
          </div>
        </div>

      </div>
    </section>
  );
};

export default About;
